import { IS_MISSED, IS_NOT_ON_BOARD, IS_ON_BOARD, MARKER_STATES, WAY_TO } from 'constants/States';
import moment from 'moment';
import { MARKER_TYPE, SCHEDULE_SERVER_TIME_FORMAT } from 'constants/Values';

export function movePupilsToExpectedStations(way) {
  if (!way || !way.stations) return way;

  // Create a map to store pupils under their expected stations
  const stationMap = way.stations.reduce((map, station) => {
    // eslint-disable-next-line no-param-reassign
    map[station.id] = { ...station, stationPupils: [] };
    return map;
  }, {});

  // Iterate through all stations and reassign pupils to their expected station
  way.stations.forEach(station => {
    station.stationPupils.forEach(pupil => {
      const expectedStation = stationMap[pupil.expectedStationId || station.id]; // not run legs have no expected id
      if (expectedStation) {
        // Check if the pupil is currently at their expected station
        if (pupil.expectedStationId && station.id !== pupil.expectedStationId) {
          // Add deviation information only if the current station is not the expected station
          expectedStation.stationPupils.push({
            ...pupil,
            deviation: {
              ...station,
              active: false,
              stationPupils: undefined, // Exclude stationPupils field to avoid recursion
            },
          });
        } else {
          // If the pupil is at their expected station, just assign them without deviation
          expectedStation.stationPupils.push(pupil);
        }
      }
    });
  });

  // Remove any unplanned stations (i.e., stations with no planedArrivalTime)
  return {
    ...way,
    stations: Object.values(stationMap).filter(station => station.planedArrivalTime !== null),
  };
}

export function stationScheduleSort(a, b) {
  if (!a.parsedTime)
    // eslint-disable-next-line no-param-reassign
    a.parsedTime = moment(a.planedArrivalTime, SCHEDULE_SERVER_TIME_FORMAT).valueOf();
  if (!b.parsedTime)
    // eslint-disable-next-line no-param-reassign
    b.parsedTime = moment(b.planedArrivalTime, SCHEDULE_SERVER_TIME_FORMAT).valueOf();

  return a.parsedTime - b.parsedTime;
}

export function stationStopOrderSort(a, b) {
  if (!a.parsedArrivalTime && a.arrivalTime) {
    // eslint-disable-next-line no-param-reassign
    a.parsedArrivalTime = moment(a.arrivalTime, SCHEDULE_SERVER_TIME_FORMAT).valueOf();
  }
  if (!b.parsedArrivalTime && b.arrivalTime) {
    // eslint-disable-next-line no-param-reassign
    b.parsedArrivalTime = moment(b.arrivalTime, SCHEDULE_SERVER_TIME_FORMAT).valueOf();
  }

  if (a.parsedArrivalTime && b.parsedArrivalTime) {
    // Both have arrival times, sort by it
    return a.parsedArrivalTime - b.parsedArrivalTime;
  }

  if (!a.parsedArrivalTime && !b.parsedArrivalTime) {
    // Neither have arrival times, maintain their planned order
    return a.stationOrder - b.stationOrder;
  }

  // One has an arrival time and the other doesn't
  return a.parsedArrivalTime ? -1 : 1;
}

export function sortVisitedAndUnvisitedStations(stations) {
  // Precompute arrival times only once
  stations.forEach(station => {
    if (!station.parsedArrivalTime && station.arrivalTime) {
      // eslint-disable-next-line no-param-reassign
      station.parsedArrivalTime = moment(station.arrivalTime).valueOf();
    }
  });

  // Extract and sort visited stations by arrival time
  const visitedStations = stations
    .filter(station => station.parsedArrivalTime)
    .sort((a, b) => a.parsedArrivalTime - b.parsedArrivalTime);

  // Merge back while keeping unvisited stations in their original positions
  let visitedIndex = 0;
  return stations.map(
    // eslint-disable-next-line no-plusplus
    station => (station.parsedArrivalTime ? visitedStations[visitedIndex++] : station),
  );
}

function getStateForMarker(pupil, direction) {
  if (pupil.delivered) {
    if (direction === WAY_TO) {
      return MARKER_STATES.ON_BOARDED;
    }
    return MARKER_STATES.DELIVERED;
  } else if (pupil.state === IS_ON_BOARD && direction === WAY_TO) {
    return MARKER_STATES.ON_BOARDED;
  } else if (pupil.absent) {
    return MARKER_STATES.ABSENT;
  } else if (pupil.state === IS_MISSED) {
    return MARKER_STATES.MISSED;
  }
  return MARKER_STATES.SCHEDULED;
}

export function createStationMarkerInfo(stations, direction, plannedStationOrder) {
  if (!stations) return [];

  // Helper function to generate marker info for a station
  const generateMarkerInfo = (station, pupils, hasActiveDeviation) => ({
    ...station, // Include all station properties
    pupils,
    hasActiveDeviation,
  });

  // Map to store markers by station ID
  const markerMap = new Map();

  // Process each station
  stations.forEach(station => {
    const plannedStationNumber = plannedStationOrder.indexOf(station.id) + 1;
    const plannedStationStatus = station.state;

    // Prepare data for the planned pupils
    const plannedPupils = station.stationPupils.map(pupil => ({
      ...pupil,
      stateForMarker: getStateForMarker(pupil, direction),
      deviation: pupil.deviation || null, // Include deviation if it exists
    }));

    // Group pupils with deviations to their deviation location
    plannedPupils.forEach(pupil => {
      const { deviation } = pupil;
      if (deviation) {
        // Pupil has a deviation, move them to the deviation station
        if (!markerMap.has(deviation.id)) {
          // Create a new marker for the deviation station
          markerMap.set(
            deviation.id,
            generateMarkerInfo(
              deviation,
              [
                {
                  ...pupil,
                  plannedStationNumber,
                  plannedStationStatus,
                  deviation: null,
                },
              ], // Remove deviation info for this pupil
              deviation.active,
            ),
          );
        } else {
          // Update existing marker for the deviation station
          const existingMarker = markerMap.get(deviation.id);
          existingMarker.pupils.push({
            ...pupil,
            plannedStationNumber,
            plannedStationStatus,
            deviation: null,
          });
          existingMarker.hasActiveDeviation = existingMarker.hasActiveDeviation || deviation.active;
        }
      }
      // Pupil has no deviation, keep them at the planned station
      else if (!markerMap.has(station.id)) {
        markerMap.set(
          station.id,
          generateMarkerInfo(
            station,
            [{ ...pupil }], // Add pupil as is
            false,
          ),
        );
      } else {
        const existingMarker = markerMap.get(station.id);
        existingMarker.pupils.push(pupil);
      }
    });

    // Add station to marker map if it doesn't exist (for planned pupils)
    if (!markerMap.has(station.id)) {
      markerMap.set(
        station.id,
        generateMarkerInfo(
          station,
          [],
          false, // Default no active deviation
        ),
      );
    }
  });

  // Convert the marker map to an array
  return Array.from(markerMap.values());
}

// DEBUG
const randomMap = [];
let debugi = 0;
function getI() {
  return debugi;
}
function incI() {
  debugi += 1;
  return debugi;
}
function resetI() {
  debugi = -1;
  return debugi;
}

export const DEBUG_DATA = false;
export function createDebugData(selectedWayDetails) {
  resetI();
  // eslint-disable-next-line no-param-reassign
  selectedWayDetails.stations = selectedWayDetails.stations.map(station => ({
    ...station,
    stationPupils: station.stationPupils.map(pupil => {
      const updatePupil = { ...pupil };
      updatePupil.state = IS_NOT_ON_BOARD;
      const otherState = randomMap[incI()] || (randomMap[getI()] = Math.random());
      updatePupil.state =
        // eslint-disable-next-line no-nested-ternary
        otherState >= 0.4 ? (otherState < 0.5 ? IS_MISSED : IS_ON_BOARD) : updatePupil.state;
      if (updatePupil.state === IS_NOT_ON_BOARD) {
        updatePupil.absent = (randomMap[incI()] || (randomMap[getI()] = Math.random())) < 0.2;
        if (!updatePupil.absent) {
          updatePupil.delivered = (randomMap[incI()] || (randomMap[getI()] = Math.random())) < 0.5;
        }
      }
      return updatePupil;
    }),
  }));
}

export class ArrowUtility {
  // Helper function to convert LatLng to pixel coordinates
  static latLngToPixel(latLng, projection, scale) {
    const worldPoint = projection.fromLatLngToPoint(latLng);
    return {
      x: worldPoint.x * scale,
      y: worldPoint.y * scale,
    };
  }

  // Helper function to convert pixel coordinates back to LatLng
  static pixelToLatLng(pixel, projection, scale) {
    const worldPoint = {
      x: pixel.x / scale,
      y: pixel.y / scale,
    };
    return projection.fromPointToLatLng(worldPoint);
  }

  static determineClosestEdge(angle) {
    if (angle >= -Math.PI / 4 && angle < Math.PI / 4) return 'left';
    if (angle >= Math.PI / 4 && angle < (3 * Math.PI) / 4) return 'top';
    if (angle >= -(3 * Math.PI) / 4 && angle < -Math.PI / 4) return 'bottom';
    return 'right';
  }

  static applyEdgeOffset(pixel, edge, boxWidth, boxHeight) {
    switch (edge) {
      case 'top':
        return { x: pixel.x, y: pixel.y - boxHeight / 2 };
      case 'bottom':
        return { x: pixel.x, y: pixel.y + boxHeight / 2 };
      case 'left':
        return { x: pixel.x - boxWidth / 2, y: pixel.y };
      case 'right':
        return { x: pixel.x + boxWidth / 2, y: pixel.y };
      default:
        return pixel; // No adjustment if edge is not recognized
    }
  }

  /**
   * Calculates the arrow shaft and head positions between markers[i] and markers[i+1].
   * @param {Array} markers - Array of marker objects with position and markerIcon properties.
   * @param {Object} map - Google Maps instance (for projection and zoom scaling).
   * @returns {Array} - Array of arrow objects with shaft and head paths.
   */
  static calculateArrows(markers, map) {
    if (!map) throw new Error('Google Maps instance is required.');

    const projection = map.getProjection();
    if (!projection) return [];

    // eslint-disable-next-line no-restricted-properties
    const scale = Math.pow(2, map.getZoom()); // Map scale at current zoom level

    // Calculate arrow positions for each marker pair
    const arrows = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < markers.length - 1; i++) {
      const startMarker = markers[i];
      const endMarker = markers[i + 1];

      const start = startMarker.position;
      const end = endMarker.position;

      // Adjust start and end positions based on marker types
      const adjustedStart = ArrowUtility.calculateStartOffset(
        start,
        startMarker,
        projection,
        scale,
        end,
      );
      const adjustedEnd = ArrowUtility.calculateEndOffset(end, endMarker, projection, scale, start);

      // Calculate arrowhead points
      const arrowHead = ArrowUtility.calculateArrowhead(
        adjustedStart,
        adjustedEnd,
        8,
        6,
        projection,
        scale,
      );

      arrows.push({
        shaft: [adjustedStart, adjustedEnd], // Arrow shaft path
        head: arrowHead, // Arrowhead path
      });
    }

    return arrows;
  }

  /**
   * Adjusts the start of the arrow shaft based on the stop type.
   * @param {google.maps.LatLng} start - Start position (LatLng).
   * @param {Object} marker - Marker object.
   * @param {Object} projection - Map projection.
   * @param {number} scale - Map scale at current zoom level.
   * @param {google.maps.LatLng} end - End position (LatLng).
   * @returns {google.maps.LatLng} - Adjusted start position (LatLng).
   */
  static calculateStartOffset(start, marker, projection, scale, end) {
    const { type } = marker;

    if (type === MARKER_TYPE.PLANNED_STATION) {
      const boxWidth = 30; // Box width in pixels
      const boxHeight = 20; // Box height in pixels

      const startPixel = ArrowUtility.latLngToPixel(start, projection, scale);
      const endPixel = ArrowUtility.latLngToPixel(end, projection, scale);

      // Calculate angle and determine closest edge
      const angle = Math.atan2(startPixel.y - endPixel.y, startPixel.x - endPixel.x);
      const closestEdge = ArrowUtility.determineClosestEdge(angle);

      // Adjust start pixel based on closest edge
      const adjustedPixel = ArrowUtility.applyEdgeOffset(
        startPixel,
        closestEdge,
        boxWidth,
        boxHeight,
      );

      return ArrowUtility.pixelToLatLng(adjustedPixel, projection, scale);
    }

    // Default for unplanned stops
    return start;
  }

  /**
   * Adjusts the end of the arrow shaft based on the stop type.
   * @param {google.maps.LatLng} end - End position (LatLng).
   * @param {Object} marker - Marker object.
   * @param {Object} projection - Map projection.
   * @param {number} scale - Map scale at current zoom level.
   * @param {google.maps.LatLng} start - Start position (LatLng).
   * @returns {google.maps.LatLng} - Adjusted end position (LatLng).
   */
  static calculateEndOffset(end, marker, projection, scale, start) {
    const { type } = marker;

    if (type === MARKER_TYPE.PLANNED_STATION) {
      const boxWidth = 30; // Box width in pixels
      const boxHeight = 20; // Box height in pixels

      const startPixel = ArrowUtility.latLngToPixel(start, projection, scale);
      const endPixel = ArrowUtility.latLngToPixel(end, projection, scale);

      // Calculate angle and determine closest edge
      const angle = Math.atan2(endPixel.y - startPixel.y, endPixel.x - startPixel.x);
      const closestEdge = ArrowUtility.determineClosestEdge(angle);

      // Adjust end pixel based on closest edge
      const adjustedPixel = ArrowUtility.applyEdgeOffset(
        endPixel,
        closestEdge,
        boxWidth,
        boxHeight,
      );

      return ArrowUtility.pixelToLatLng(adjustedPixel, projection, scale);
    }
    // else if (type === MARKER_TYPE.UNPLANNED_STATION) {
    //   const endPixel = ArrowUtility.latLngToPixel(end, projection, scale);
    //
    //   const adjustedPixel = {
    //     x: endPixel.x,
    //     y: endPixel.y + 37 / 2, // Adjust to bottom middle edge
    //   };
    //
    //   return ArrowUtility.pixelToLatLng(adjustedPixel, projection, scale);
    // }

    return end; // Default
  }

  /**
   * Calculates the arrowhead points for a line given its start and end positions.
   * The arrowhead is defined by its length along the shaft axis and its width
   * perpendicular to the shaft axis, both specified in pixels.
   *
   * @param {google.maps.LatLng} start - The starting position of the arrow shaft (LatLng object).
   * @param {google.maps.LatLng} end - The ending position of the arrow shaft and tip of the arrowhead (LatLng object).
   * @param {number} lengthInPixels - The length of the arrowhead from the tip to the base
   * @param {number} widthInPixels - The width of the arrowhead base (perpendicular to the shaft axis, in pixels).
   * @param {google.maps.Projection} projection - The map's projection
   * @param {number} scale - The map's scale at the current zoom level, calculated as `Math.pow(2, map.getZoom())`.
   * @returns {google.maps.LatLng[]} - An array of LatLng points representing the arrowhead's path
   * in the following order:   *   [left corner of base, tip of arrowhead, right corner of base,
   * left corner of base (to close the path)].
   */
  static calculateArrowhead(start, end, lengthInPixels, widthInPixels, projection, scale) {
    const startPixel = ArrowUtility.latLngToPixel(start, projection, scale);
    const endPixel = ArrowUtility.latLngToPixel(end, projection, scale);

    // Calculate direction vector
    const dx = endPixel.x - startPixel.x;
    const dy = endPixel.y - startPixel.y;
    const magnitude = Math.sqrt(dx * dx + dy * dy);

    // Normalize direction vector
    const unitX = dx / magnitude;
    const unitY = dy / magnitude;

    // Arrowhead tip
    const tip = endPixel;

    // Base of the arrowhead (along the shaft axis)
    const baseX = tip.x - lengthInPixels * unitX;
    const baseY = tip.y - lengthInPixels * unitY;

    // Perpendicular vector for width
    const perpX = -unitY;
    const perpY = unitX; // this is correct to rotate by 90degrees

    // Left and right corners of the arrowhead base
    const leftX = baseX - (widthInPixels / 2) * perpX;
    const leftY = baseY - (widthInPixels / 2) * perpY;

    const rightX = baseX + (widthInPixels / 2) * perpX;
    const rightY = baseY + (widthInPixels / 2) * perpY;

    // Convert back to LatLng
    const tipLatLng = ArrowUtility.pixelToLatLng(tip, projection, scale);
    const leftLatLng = ArrowUtility.pixelToLatLng({ x: leftX, y: leftY }, projection, scale);
    const rightLatLng = ArrowUtility.pixelToLatLng({ x: rightX, y: rightY }, projection, scale);

    return [leftLatLng, tipLatLng, rightLatLng, leftLatLng]; // Closed path for arrowhead
  }
}

export function copyToClipboard(text, onSuccess = () => {}, onError = () => {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.error('Clipboard functions are only available in the browser.');
    return;
  }

  if (navigator.clipboard && window.isSecureContext) {
    // Modern method (async)
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log('Text copied to clipboard');
        onSuccess();
      })
      .catch(err => {
        console.error('Async clipboard API failed', err);
        onError(err);
      });
  } else {
    // Fallback for older browsers (IE11)
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Text copied to clipboard');
        onSuccess();
      } else {
        throw new Error('Document.execCommand failed');
      }
    } catch (err) {
      console.error('Fallback copy failed', err);
      onError(err);
    }
    document.body.removeChild(textArea);
  }
}

/**
 * Haversine formula to calculate great-circle distance between two points.
 * Returns distance in meters.
 */
function haversineDistance(coord1, coord2) {
  const R = 6371000; // Earth radius in meters
  const toRadians = deg => (deg * Math.PI) / 180;

  const lat1 = toRadians(coord1.lat());
  const lat2 = toRadians(coord2.lat());
  const deltaLat = lat2 - lat1;
  const deltaLng = toRadians(coord2.lng() - coord1.lng());

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Calculates the angle between three points to detect sharp turns.
 */
function calculateAngle(p1, p2, p3) {
  const dx1 = p2.lng - p1.lng;
  const dy1 = p2.lat - p1.lat;
  const dx2 = p3.lng - p2.lng;
  const dy2 = p3.lat - p2.lat;

  const dotProduct = dx1 * dx2 + dy1 * dy2;
  const magnitude1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
  const magnitude2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

  return (Math.acos(dotProduct / (magnitude1 * magnitude2)) * 180) / Math.PI;
}

/**
 * Determines if a marker is a turning point by checking angle changes.
 */
function isTurningPoint(markers, index) {
  if (index <= 0 || index >= markers.length - 1) return false;

  const prev = markers[index - 1].position;
  const curr = markers[index].position;
  const next = markers[index + 1].position;

  const angle = calculateAngle(prev, curr, next);
  return Math.abs(angle) > 30; // Threshold for significant turns
}

export function clusterLocationHistoryMarkers(markers, map) {
  if (!map || markers.length <= 2) return markers;

  const projection = map.getProjection();
  if (!projection) return markers;

  const zoom = map.getZoom();
  // const geoThreshold = Math.max(10, 600 - zoom * 40); // Dynamic threshold

  let geoThreshold;

  if (zoom < 8) {
    geoThreshold = 6480;
  } else if (zoom === 8) {
    geoThreshold = 4320;
  } else if (zoom === 9) {
    geoThreshold = 2880;
  } else if (zoom === 10) {
    geoThreshold = 1920;
  } else if (zoom === 11) {
    geoThreshold = 960;
  } else if (zoom === 12) {
    geoThreshold = 480;
  } else if (zoom === 13) {
    geoThreshold = 240;
  } else if (zoom === 14) {
    geoThreshold = 120;
  } else if (zoom === 15) {
    geoThreshold = 60;
  } else if (zoom === 16) {
    geoThreshold = 30;
  } else if (zoom === 17) {
    geoThreshold = 20;
  } else {
    geoThreshold = 10;
  }

  const turnMinDistance = 20; // Base threshold before considering turns

  const groupedMarkers = [];
  const seen = new Set();

  groupedMarkers.push(markers[0]); // Always keep first marker
  seen.add(markers[0].markerId);

  let lastKeptMarker = markers[0];

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < markers.length - 1; i++) {
    const marker = markers[i];
    if (marker.type !== MARKER_TYPE.LOCATION_HISTORY || seen.has(marker.markerId)) {
      groupedMarkers.push(marker);
      seen.add(marker.markerId);
      // eslint-disable-next-line no-continue
      continue;
    }

    const distance = haversineDistance(lastKeptMarker.position, marker.position);
    if (distance >= geoThreshold || (distance >= turnMinDistance && isTurningPoint(markers, i))) {
      groupedMarkers.push(marker);
      seen.add(marker.markerId);
      lastKeptMarker = marker;
    }
  }

  groupedMarkers.push(markers[markers.length - 1]); // Always keep last marker

  return groupedMarkers;
}
